import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/button/Button';
import { StyledNavLink } from 'components/link/StyledNavLink';
import styled, { css } from 'styled-components';

export const dropdownButtonPaddingX = '1.2rem';
export const dropdownButtonPaddingY = '0.6rem';

export type DropdownPaddingOverride = 'none' | '1.5y' | { x: string; y: string };

function computePaddingVars(padding: DropdownPaddingOverride | undefined) {
    switch (padding) {
        case 'none': {
            return '';
        }
        case '1.5y': {
            return css`
                --padding-x: ${dropdownButtonPaddingX};
                --padding-y: calc(1.5 * ${dropdownButtonPaddingY});
            `;
        }
        case undefined: {
            return css`
                --padding-x: ${dropdownButtonPaddingX};
                --padding-y: ${dropdownButtonPaddingY};
            `;
        }
        default: {
            return css`
                --padding-x: ${padding.x};
                --padding-y: ${padding.y};
            `;
        }
    }
}

type DropdownButtonProps = { $paddingOverride?: DropdownPaddingOverride };
export const DropdownButton = styled(Button)<Readonly<DropdownButtonProps>>`
    cursor: pointer;
    background: none;
    text-align: left;
    color: var(--text-primary);
    border: none;
    ${(props) => computePaddingVars(props.$paddingOverride)}
    padding-left: var(--padding-x);
    padding-right: var(--padding-x);
    padding-top: 0;
    padding-bottom: 0;
`;

export const ButtonLabel = styled.div`
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    align-content: center;
    align-items: center;
`;

export const ButtonLabelText = styled.div<{ $isPlaceholder?: boolean }>`
    flex-grow: 1;
    padding-top: var(--padding-y);
    padding-bottom: var(--padding-y);

    color: ${({ $isPlaceholder }) =>
        $isPlaceholder ? 'var(--text-secondary)' : 'inherit'};
`;

export const dropdownTriggerIconWidthEm = 0.875;

const getDropdownTriggerIconLeftPaddingEm = (reduceLeftPadding?: boolean) =>
    reduceLeftPadding ? 0.6 : 1;
export const getDropdownTriggerIconFullWidth = (reduceLeftPadding?: boolean) =>
    `${
        dropdownTriggerIconWidthEm +
        getDropdownTriggerIconLeftPaddingEm(reduceLeftPadding)
    }em`;

export const ButtonLabelIcon = styled(FontAwesomeIcon)<{ $reduceLeftPadding?: boolean }>`
    padding-left: ${({ $reduceLeftPadding }) =>
        getDropdownTriggerIconLeftPaddingEm($reduceLeftPadding)}em;
    color: ${({ color }) => color ?? 'var(--icon-action-active)'};
    font-size: 1.4rem;
    width: ${dropdownTriggerIconWidthEm}em;
    // The icon will render outside its box if this is not specified
    box-sizing: content-box !important;
`;

export const ButtonLabelIconGroup = styled(FontAwesomeIcon)`
    padding-left: 1rem;
    color: var(--icon-action-active);
    padding-top: 0.5rem;
    font-size: 12px;
`;

export const DropdownItemLink = styled(StyledNavLink)`
    display: flex;
    justify-content: space-between;
`;

export const iconButtonInteractiveStyles = css`
    font-size: 1.4rem;
    border: 1px solid transparent;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;

    &:hover,
    &:focus-visible {
        border-color: var(--nav-border);
        background-color: var(--action-selected);
    }
    &:active {
        border-color: var(--text-primary);
    }
`;

export const ClearButton = styled.div`
    ${iconButtonInteractiveStyles}
    display: flex;
    align-items: center;
    justify-content: center;
`;
